import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  BicycleIcon,
  CameraIcon,
  CheckCircleIcon,
  HeartIcon,
  MapPinIcon,
  MountainsIcon,
  PencilSimpleIcon,
  SlidersHorizontalIcon,
  StarIcon,
  TrashIcon,
  UserCircleIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { AppShell, Surface } from "@/components/layout/AppShell";
import { Skeleton } from "@/components/ui/skeleton";
import { HomeLocationCard } from "@/components/HomeLocationCard";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { parkPath } from "@/lib/routes";
import {
  DESTINATION_PREFERENCE_OPTIONS,
  RIDER_LEVEL_OPTIONS,
  RIDING_CONTEXT_OPTIONS,
  RIDING_STYLE_OPTIONS,
  emptyRiderProfileForm,
  formToRiderProfileInput,
  hasRiderProfilePreferences,
  profileToForm,
  toggleProfileValue,
} from "@/lib/riderProfile";

const TAB_OPTIONS = [
  { id: "profile", label: "Profile", icon: UserCircleIcon },
  { id: "preferences", label: "Ride Preferences", icon: SlidersHorizontalIcon },
];

const stateBadges = (state = {}) => [
  state.want_to_ride ? "Want to Ride" : null,
  state.visited ? "Visited" : null,
  state.favorite ? "Favorite" : null,
].filter(Boolean);

function ChoiceChip({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
        active
          ? "border-[#CCFF00] bg-[#CCFF00] text-black shadow-[0_0_22px_rgba(204,255,0,0.08)]"
          : "border-white/10 bg-white/[0.04] text-zinc-300 hover:border-white/20 hover:bg-white/[0.08]"
      }`}
    >
      {children}
    </button>
  );
}

function PreferenceCard({ icon: Icon, title, description, children }) {
  return (
    <section className="group relative overflow-hidden rounded-3xl border border-white/10 bg-[linear-gradient(180deg,#151517_0%,#111113_100%)] p-5 shadow-[0_18px_50px_rgba(0,0,0,0.14)] transition-colors hover:border-white/15 md:p-6">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#CCFF00]/10 bg-[#CCFF00]/[0.06] text-[#CCFF00]">
          <Icon size={20} weight="bold" />
        </span>
        <div>
          <h2 className="font-display text-xl font-bold uppercase text-white">{title}</h2>
          {description && <p className="mt-1 text-sm text-zinc-400">{description}</p>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function SummaryTag({ children }) {
  return (
    <span className="rounded-full border border-white/10 bg-white/[0.045] px-3 py-1.5 text-xs font-bold text-zinc-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
      {children}
    </span>
  );
}

function ProfilePhotoUploadControl({ busy, disabled = false, onUploadPhoto, label = "Add photo", compact = false }) {
  const blocked = busy || disabled;
  return (
    <label className={`inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-xl font-extrabold transition-colors ${
      compact
        ? "border border-white/10 bg-black/65 px-3 py-2 text-[11px] text-white backdrop-blur hover:bg-black/80"
        : "bg-[#CCFF00] px-3 py-2 text-xs text-black"
    } ${blocked ? "pointer-events-none opacity-45" : ""}`}>
      <CameraIcon size={15} weight="bold" /> {label}
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        disabled={blocked}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onUploadPhoto(file);
          event.target.value = "";
        }}
      />
    </label>
  );
}

function ProfileTab({
  user,
  form,
  personalMap,
  garage,
  garageBusy,
  homeLocation,
  onHomeLocationChange,
  onPreferences,
  onVerifyEmail,
  sendingVerification,
  onUploadPhoto,
  onSetHero,
  onRemovePhoto,
  onSaveBike,
  onRemoveBike,
}) {
  const navigate = useNavigate();
  const stats = useMemo(() => ({
    visited: personalMap.filter((entry) => entry.state?.visited).length,
    want: personalMap.filter((entry) => entry.state?.want_to_ride).length,
    favorite: personalMap.filter((entry) => entry.state?.favorite).length,
  }), [personalMap]);

  const levelLabel = translateLabel(RIDER_LEVEL_OPTIONS.find((item) => item.value === form.level)?.label || "Not set");
  const displayName = user.name || user.email?.split("@")[0] || translateLabel("Rider");
  const initials = displayName.slice(0, 1).toUpperCase();
  const heroPhoto = garage.photos.find((photo) => photo.is_hero) || garage.photos[0] || null;
  const [savedFilter, setSavedFilter] = useState("all");
  const [editingBikeId, setEditingBikeId] = useState(null);
  const [bikeFormOpen, setBikeFormOpen] = useState(false);
  const [bikeForm, setBikeForm] = useState({ name: "", category: "", year: "", photo_id: "" });

  const savedByFilter = useMemo(() => {
    const matches = {
      all: () => true,
      visited: (entry) => entry.state?.visited,
      want: (entry) => entry.state?.want_to_ride,
      favorites: (entry) => entry.state?.favorite,
    };
    return personalMap.filter(matches[savedFilter] || matches.all).slice(0, 6);
  }, [personalMap, savedFilter]);
  const submitBike = async (event) => {
    event.preventDefault();
    if (!bikeForm.name.trim()) return;
    await onSaveBike({
      id: editingBikeId || undefined,
      name: bikeForm.name.trim(),
      category: bikeForm.category.trim() || null,
      year: bikeForm.year ? Number(bikeForm.year) : null,
      photo_id: bikeForm.photo_id || null,
    });
    setEditingBikeId(null);
    setBikeFormOpen(false);
    setBikeForm({ name: "", category: "", year: "", photo_id: "" });
  };

  return (
    <div className="min-w-0 space-y-4 overflow-x-hidden md:space-y-5">
      <section className="overflow-hidden rounded-3xl border border-white/10 bg-[#111113] shadow-[0_18px_55px_rgba(0,0,0,0.22)] md:hidden">
        <div className="relative h-44 overflow-hidden bg-[#0d0d0f]">
          {heroPhoto ? (
            <img src={heroPhoto.url} alt="" className="h-full w-full object-cover object-center" />
          ) : (
            <div className="h-full w-full bg-[radial-gradient(circle_at_72%_24%,rgba(204,255,0,0.14),transparent_30%),linear-gradient(135deg,#18181b,#09090b)]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#111113] via-black/15 to-transparent" />
          <div className="absolute inset-y-0 left-0 w-1 bg-[#CCFF00]" />
          <div className="absolute right-3 top-3 z-10">
            <ProfilePhotoUploadControl
              compact
              busy={garageBusy}
              disabled={garage.photos.length >= garage.photo_limit}
              onUploadPhoto={onUploadPhoto}
              label={translateLabel(heroPhoto ? "Change photo" : "Add photo")}
            />
          </div>
        </div>

        <div className="relative px-4 pb-4">
          <div className="-mt-10 flex items-end gap-3">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border-4 border-[#111113] bg-[#CCFF00] font-display text-3xl font-black text-black shadow-xl">
              {initials}
            </div>
            <div className="min-w-0 flex-1 pb-1">
              <div className="flex min-w-0 items-center gap-2">
                <h2 className="truncate font-display text-3xl font-black uppercase leading-none text-white">{displayName}</h2>
              </div>
              <p className="mt-1 truncate text-xs font-medium text-zinc-400">{user.email}</p>
            </div>
          </div>

          {!user.email_verified && (
            <button
              type="button"
              onClick={onVerifyEmail}
              disabled={sendingVerification}
              className="mt-3 rounded-full border border-amber-400/25 bg-amber-400/[0.07] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-amber-300 disabled:opacity-50"
            >
              {translateLabel(sendingVerification ? "Sending…" : "Verify email")}
            </button>
          )}

          <div className="mt-4 grid grid-cols-2 gap-2">
            {[
              ["Level", levelLabel],
              ["Visited", stats.visited],
              ["Want to Ride", stats.want],
              ["Favorites", stats.favorite],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-white/[0.08] bg-black/20 px-3 py-3">
                <div className="text-[9px] font-black uppercase tracking-[0.16em] text-zinc-500">{translateLabel(label)}</div>
                <div className="mt-1 truncate font-display text-lg font-black text-white">{value}</div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={onPreferences}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#CCFF00] px-4 py-3 text-sm font-extrabold text-black"
          >
            <SlidersHorizontalIcon size={16} weight="bold" /> {translateLabel("Tune ride preferences")}
          </button>
        </div>
      </section>

      <section className="relative hidden min-h-[320px] overflow-hidden rounded-[2rem] border border-white/10 bg-[#0d0d0f] shadow-[0_24px_80px_rgba(0,0,0,0.28)] md:block">
        {heroPhoto ? (
          <>
            <img
              src={heroPhoto.url}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-[72%_center]"
            />
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(8,8,10,0.98)_0%,rgba(8,8,10,0.9)_34%,rgba(8,8,10,0.48)_66%,rgba(8,8,10,0.14)_100%)]" />
          </>
        ) : (
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_22%,rgba(204,255,0,0.12),transparent_28%),linear-gradient(135deg,#121214,#09090b)]" />
        )}
        <div className="absolute inset-y-0 left-0 w-1 bg-[#CCFF00]" />
        <div className="absolute right-5 top-5 z-20">
          <ProfilePhotoUploadControl
            compact
            busy={garageBusy}
            disabled={garage.photos.length >= garage.photo_limit}
            onUploadPhoto={onUploadPhoto}
            label={translateLabel(heroPhoto ? "Change profile photo" : "Add profile photo")}
          />
        </div>
        <div className="relative flex min-h-[320px] max-w-[68%] flex-col justify-center p-9 lg:p-10">
          <div className="flex items-end gap-4">
            <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-[1.7rem] border border-[#CCFF00]/30 bg-[#CCFF00] font-display text-4xl font-black text-black shadow-[0_18px_50px_rgba(204,255,0,0.14)]">
              {initials}
            </div>
            <div className="min-w-0 pb-1">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-display text-5xl font-black uppercase leading-none text-white">{displayName}</h2>
                {!user.email_verified && (
                  <button
                    type="button"
                    onClick={onVerifyEmail}
                    disabled={sendingVerification}
                    className="rounded-full border border-amber-400/25 bg-black/45 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-amber-300 backdrop-blur disabled:opacity-50"
                  >
                    {sendingVerification ? "Sending…" : "Verify email"}
                  </button>
                )}
              </div>
              <p className="mt-2 truncate text-sm text-zinc-400">{user.email}</p>
            </div>
          </div>

          <div className="mt-7 grid max-w-2xl grid-cols-4 gap-2">
            {[
              ["Level", levelLabel],
              ["Visited", stats.visited],
              ["Want to Ride", stats.want],
              ["Favorites", stats.favorite],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-white/10 bg-black/35 px-4 py-3 backdrop-blur-md">
                <div className="text-[9px] font-black uppercase tracking-[0.17em] text-zinc-500">{translateLabel(label)}</div>
                <div className="mt-1 truncate font-display text-lg font-black text-white">{value}</div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={onPreferences}
            className="mt-5 inline-flex w-fit items-center gap-2 rounded-full border border-[#CCFF00]/30 bg-[#CCFF00]/10 px-4 py-2 text-xs font-extrabold text-[#CCFF00] backdrop-blur transition-colors hover:bg-[#CCFF00]/15"
          >
            <SlidersHorizontalIcon size={15} weight="bold" /> Tune ride preferences
          </button>
        </div>
      </section>

      <div className="min-w-0 space-y-5">
        <div className="grid min-w-0 gap-5 xl:grid-cols-2">
        <section className="min-w-0 rounded-2xl border border-white/10 bg-[linear-gradient(180deg,#151517,#111113)] p-4 shadow-[0_18px_50px_rgba(0,0,0,0.12)] md:rounded-3xl md:p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#CCFF00]">{translateLabel("Your riding identity")}</div>
              <h2 className="mt-1 font-display text-2xl font-bold uppercase text-white">{translateLabel("Riding profile")}</h2>
            </div>
            <button type="button" onClick={onPreferences} className="text-sm font-bold text-[#CCFF00] hover:underline">
              {translateLabel("Edit")}
            </button>
          </div>

          <div className="mt-5 space-y-5">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{translateLabel("Preferred riding")}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {form.riding_styles.length
                  ? form.riding_styles.map((value) => <SummaryTag key={value}>{translateLabel(value)}</SummaryTag>)
                  : <span className="text-sm text-zinc-500">{translateLabel("Not set yet")}</span>}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{translateLabel("Typical crew")}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {form.riding_contexts.length
                  ? form.riding_contexts.map((value) => <SummaryTag key={value}>{translateLabel(value)}</SummaryTag>)
                  : <span className="text-sm text-zinc-500">{translateLabel("Not set yet")}</span>}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{translateLabel("Destination preferences")}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {form.destination_preferences.length
                  ? form.destination_preferences.map((value) => <SummaryTag key={value}>{translateLabel(value)}</SummaryTag>)
                  : <span className="text-sm text-zinc-500">{translateLabel("Not set yet")}</span>}
              </div>
            </div>
          </div>
        </section>
        <HomeLocationCard value={homeLocation} onChange={onHomeLocationChange} />
        <section className="min-w-0 rounded-2xl border border-white/10 bg-[#121214] p-4 md:rounded-3xl md:p-6">
          <div className="flex items-center gap-2 text-[#CCFF00]">
            <BicycleIcon size={18} weight="bold" />
            <span className="text-[11px] font-bold uppercase tracking-[0.18em]">{translateLabel("Bike Garage")}</span>
          </div>
          <h2 className="mt-2 font-display text-2xl font-bold uppercase text-white">{translateLabel("Your bikes")}</h2>
          <p className="mt-1 text-sm text-zinc-400">{translateLabel("Keep up to {count} bikes on your profile.", { count: garage.bike_limit })}</p>

          {garage.bikes.length > 0 && (
            <div className="mt-4 space-y-3">
              {garage.bikes.map((bike) => (
                <div key={bike.id} className="group relative flex min-w-0 items-center gap-3 overflow-hidden rounded-2xl border border-white/10 bg-[linear-gradient(110deg,rgba(255,255,255,0.035),rgba(255,255,255,0.012))] p-3 sm:gap-4 sm:p-3.5">
                  <div className="h-16 w-20 shrink-0 overflow-hidden rounded-xl border border-white/[0.06] bg-white/5 sm:h-20 sm:w-24">
                    {bike.photo_url ? <img src={bike.photo_url} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <BicycleIcon size={30} className="m-auto mt-6 text-zinc-600" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="min-w-0 truncate font-display text-base font-black uppercase text-white sm:text-lg">{bike.name}</div>
                    <div className="mt-1 text-[11px] font-bold uppercase tracking-wide text-zinc-500">{[bike.category, bike.year].filter(Boolean).join(" · ") || "Bike"}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingBikeId(bike.id);
                      setBikeFormOpen(true);
                      setBikeForm({
                        name: bike.name || "",
                        category: bike.category || "",
                        year: bike.year ? String(bike.year) : "",
                        photo_id: bike.photo_id || "",
                      });
                    }}
                    className="rounded-lg p-2 text-zinc-500 hover:bg-white/5 hover:text-[#CCFF00]"
                    aria-label={`${translateLabel("Edit")} ${bike.name}`}
                  >
                    <PencilSimpleIcon size={17} />
                  </button>
                  <button type="button" onClick={() => onRemoveBike(bike.id)} className="rounded-lg p-2 text-zinc-500 hover:bg-white/5 hover:text-red-300" aria-label={translateLabel("Remove bike")}>
                    <TrashIcon size={17} />
                  </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!bikeFormOpen ? (
            <button
              type="button"
              onClick={() => {
                setEditingBikeId(null);
                setBikeForm({ name: "", category: "", year: "", photo_id: "" });
                setBikeFormOpen(true);
              }}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-[#CCFF00]/25 bg-[#CCFF00]/[0.07] px-4 py-3 text-sm font-extrabold text-[#CCFF00] transition-colors hover:bg-[#CCFF00]/10"
            >
              + {translateLabel("Add bike")}
            </button>
          ) : (
            <form onSubmit={submitBike} className="mt-4 grid gap-2 rounded-2xl border border-white/[0.06] bg-black/10 p-3">
              <div className="mb-1 flex items-center justify-between gap-3">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#CCFF00]">
                  {translateLabel(editingBikeId ? "Editing bike" : "Add bike")}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setEditingBikeId(null);
                    setBikeFormOpen(false);
                    setBikeForm({ name: "", category: "", year: "", photo_id: "" });
                  }}
                  className="text-xs font-bold text-zinc-400 hover:text-white"
                >
                  {translateLabel("Cancel")}
                </button>
              </div>

              <input
                value={bikeForm.name}
                onChange={(event) => setBikeForm((current) => ({ ...current, name: event.target.value }))}
                placeholder={translateLabel("Bike name / model")}
                maxLength={80}
                className="rounded-xl border border-white/10 bg-black/20 px-3 py-2.5 text-sm text-white outline-none focus:border-[#CCFF00]/40"
              />
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input
                  value={bikeForm.category}
                  onChange={(event) => setBikeForm((current) => ({ ...current, category: event.target.value }))}
                  placeholder={translateLabel("Category, e.g. Enduro")}
                  maxLength={60}
                  className="rounded-xl border border-white/10 bg-black/20 px-3 py-2.5 text-sm text-white outline-none focus:border-[#CCFF00]/40"
                />
                <input
                  value={bikeForm.year}
                  onChange={(event) => setBikeForm((current) => ({ ...current, year: event.target.value.replace(/\D/g, "").slice(0, 4) }))}
                  placeholder={translateLabel("Year")}
                  inputMode="numeric"
                  className="rounded-xl border border-white/10 bg-black/20 px-3 py-2.5 text-sm text-white outline-none focus:border-[#CCFF00]/40"
                />
              </div>
              {garage.photos.length > 0 && (
                <select
                  value={bikeForm.photo_id}
                  onChange={(event) => setBikeForm((current) => ({ ...current, photo_id: event.target.value }))}
                  className="rounded-xl border border-white/10 bg-[#121214] px-3 py-2.5 text-sm text-white outline-none"
                >
                  <option value="">{translateLabel("No bike photo")}</option>
                  {garage.photos.map((photo, index) => (
                    <option key={photo.id} value={photo.id}>
                      {translateLabel("Profile photo {index}", { index: index + 1 })}{photo.is_hero ? ` (${translateLabel("Hero")})` : ""}
                    </option>
                  ))}
                </select>
              )}
              <button
                type="submit"
                disabled={garageBusy || !bikeForm.name.trim()}
                className="rounded-xl bg-[#CCFF00] px-4 py-2.5 text-sm font-extrabold text-black disabled:opacity-40"
              >
                {translateLabel(editingBikeId ? "Save changes" : "Save bike")}
              </button>
            </form>
          )}
        </section>
        <section className="min-w-0 rounded-2xl border border-white/10 bg-[#121214] p-4 md:rounded-3xl md:p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#CCFF00]">{translateLabel("My Map")}</div>
              <h2 className="mt-1 font-display text-2xl font-bold uppercase text-white">{translateLabel("Saved destinations")}</h2>
            </div>
            <button type="button" onClick={() => navigate("/my-map")} className="text-sm font-bold text-[#CCFF00] hover:underline">
              {translateLabel("View map")}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {[
              ["all", "All", personalMap.length],
              ["visited", "Visited", stats.visited],
              ["want", "Want to Ride", stats.want],
              ["favorites", "Favorites", stats.favorite],
            ].map(([id, label, count]) => (
              <button
                key={id}
                type="button"
                onClick={() => setSavedFilter(id)}
                className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
                  savedFilter === id
                    ? "border-[#CCFF00] bg-[#CCFF00] text-black"
                    : "border-white/10 bg-white/[0.04] text-zinc-300 hover:bg-white/[0.08]"
                }`}
              >
                {translateLabel(label)} · {count}
              </button>
            ))}
          </div>

          {savedByFilter.length ? (
            <div className="mt-4 divide-y divide-white/[0.07]">
              {savedByFilter.map((entry) => (
                <Link
                  key={entry.destination.id}
                  to={parkPath(entry.destination)}
                  className="group flex w-full min-w-0 items-center justify-between gap-3 rounded-xl px-2 py-3 text-left transition-colors hover:bg-white/[0.035]"
                >
                  <div className="min-w-0">
                    <div className="truncate font-bold text-white">{entry.destination.name}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {stateBadges(entry.state).map((badge) => (
                        <span key={translateLabel(badge)} className="rounded-full bg-white/[0.05] px-2 py-0.5 text-[10px] font-bold text-zinc-400">
                          {translateLabel(badge)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span className="text-zinc-600 transition-transform group-hover:translate-x-1 group-hover:text-[#CCFF00]">→</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="mt-5 rounded-xl border border-dashed border-white/10 px-4 py-8 text-center">
              <MountainsIcon size={30} className="mx-auto text-zinc-600" />
              <p className="mt-3 text-sm font-semibold text-zinc-300">{translateLabel("Nothing here yet")}</p>
              <p className="mt-1 text-xs text-zinc-500">{translateLabel("Use park actions to build this list.")}</p>
            </div>
          )}
        </section>
        </div>

        <section className="min-w-0 rounded-2xl border border-white/10 bg-[#121214] p-4 md:rounded-3xl md:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-[#CCFF00]">
                <CameraIcon size={18} weight="bold" />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em]">{translateLabel("Profile photos")}</span>
              </div>
              <h2 className="mt-2 font-display text-2xl font-bold uppercase text-white">{translateLabel("Your riding gallery")}</h2>
              <p className="mt-1 text-sm text-zinc-400">{translateLabel("Upload up to {count} photos. Pick one as your profile hero.", { count: garage.photo_limit })}</p>
            </div>
            <ProfilePhotoUploadControl
              busy={garageBusy}
              disabled={garage.photos.length >= garage.photo_limit}
              onUploadPhoto={onUploadPhoto}
            />
          </div>

          {garage.photos.length ? (
            <div className="mt-5 grid grid-cols-2 gap-2 md:grid-cols-4">
              {garage.photos.map((photo, index) => (
                <div
                  key={photo.id}
                  className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-black ${
                    photo.is_hero || (index === 0 && garage.photos.length > 2) ? "col-span-2 md:col-span-2 md:row-span-2" : ""
                  }`}
                >
                  <img
                    src={photo.url}
                    alt={translateLabel("Rider upload")}
                    className={`w-full object-cover transition-transform duration-500 group-hover:scale-[1.02] ${
                      photo.is_hero || (index === 0 && garage.photos.length > 2) ? "aspect-[16/9] h-full" : "aspect-square"
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-80" />
                  {photo.is_hero && (
                    <span className="absolute left-3 top-3 rounded-full bg-[#CCFF00] px-2.5 py-1 text-[9px] font-black uppercase tracking-wide text-black shadow-lg">
                      {translateLabel("Hero")}
                    </span>
                  )}
                  <div className="absolute inset-x-3 bottom-3 flex justify-end gap-1.5 opacity-100 md:opacity-0 md:transition-opacity md:group-hover:opacity-100">
                    {!photo.is_hero && (
                      <button type="button" onClick={() => onSetHero(photo.id)} className="rounded-xl border border-white/10 bg-black/75 p-2.5 text-[#CCFF00] backdrop-blur" aria-label={translateLabel("Set as hero")}>
                        <StarIcon size={15} weight="fill" />
                      </button>
                    )}
                    <button type="button" onClick={() => onRemovePhoto(photo.id)} className="rounded-xl border border-white/10 bg-black/75 p-2.5 text-red-300 backdrop-blur" aria-label={translateLabel("Remove photo")}>
                      <TrashIcon size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-4 rounded-xl border border-dashed border-white/10 px-4 py-8 text-center text-sm text-zinc-500">
              {translateLabel("Add your first riding or bike photo. The first photo automatically becomes your hero.")}
            </div>
          )}
        </section>

        <section className="min-w-0 rounded-2xl border border-white/10 bg-[#121214] p-4 md:rounded-3xl md:p-6">
          <div className="flex items-center gap-2 text-[#CCFF00]">
            <SlidersHorizontalIcon size={18} weight="bold" />
            <span className="text-[11px] font-bold uppercase tracking-[0.18em]">{translateLabel("How BPF uses this")}</span>
          </div>
          <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-3xl">
              <h2 className="font-display text-2xl font-bold uppercase text-white">{translateLabel("Better recommendations")}</h2>
              <p className="mt-2 text-sm leading-6 text-zinc-400">
                {translateLabel("Your profile helps BPF rank destinations and explain why a place may fit you. It does not silently hide destinations unless you apply a strict filter.")}
              </p>
            </div>
            <button
              type="button"
              onClick={onPreferences}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#CCFF00] px-5 py-3 text-sm font-extrabold text-black"
            >
              <SlidersHorizontalIcon size={16} weight="bold" /> {translateLabel("Tune ride preferences")}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function PreferencesTab({ form, setForm, saving, onSave, onClear, error }) {
  const levelLabel = RIDER_LEVEL_OPTIONS.find((item) => item.value === form.level)?.label;
  const summary = [
    levelLabel,
    ...form.riding_styles.slice(0, 2),
    ...form.riding_contexts.slice(0, 1),
    ...form.destination_preferences.slice(0, 2),
  ].filter(Boolean);

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="grid gap-5 md:grid-cols-2">
        <PreferenceCard icon={MountainsIcon} title={translateLabel("Skill comfort level")} description={translateLabel("What level best describes the trails you are comfortable riding?")}>
          <div className="flex flex-wrap gap-2">
            <ChoiceChip active={!form.level} onClick={() => setForm((current) => ({ ...current, level: "" }))}>{translateLabel("Not set")}</ChoiceChip>
            {RIDER_LEVEL_OPTIONS.map((option) => (
              <ChoiceChip
                key={option.value}
                active={form.level === option.value}
                onClick={() => setForm((current) => ({ ...current, level: option.value }))}
              >
                {translateLabel(option.label)}
              </ChoiceChip>
            ))}
          </div>
        </PreferenceCard>

        <PreferenceCard icon={BicycleIcon} title={translateLabel("Preferred riding")} description={translateLabel("Choose the riding styles you usually enjoy.")}>
          <div className="flex flex-wrap gap-2">
            {RIDING_STYLE_OPTIONS.map((value) => (
              <ChoiceChip
                key={value}
                active={form.riding_styles.includes(value)}
                onClick={() => setForm((current) => ({
                  ...current,
                  riding_styles: toggleProfileValue(current.riding_styles, value),
                }))}
              >
                {translateLabel(value)}
              </ChoiceChip>
            ))}
          </div>
        </PreferenceCard>

        <PreferenceCard icon={UsersThreeIcon} title={translateLabel("Typical crew")} description={translateLabel("Who do you usually ride with?")}>
          <div className="flex flex-wrap gap-2">
            {RIDING_CONTEXT_OPTIONS.map((value) => (
              <ChoiceChip
                key={value}
                active={form.riding_contexts.includes(value)}
                onClick={() => setForm((current) => ({
                  ...current,
                  riding_contexts: toggleProfileValue(current.riding_contexts, value),
                }))}
              >
                {translateLabel(value)}
              </ChoiceChip>
            ))}
          </div>
        </PreferenceCard>

        <PreferenceCard icon={MapPinIcon} title={translateLabel("Destination preferences")} description={translateLabel("What access or facilities matter when BPF ranks destinations?")}>
          <div className="flex flex-wrap gap-2">
            {DESTINATION_PREFERENCE_OPTIONS.map((value) => (
              <ChoiceChip
                key={value}
                active={form.destination_preferences.includes(value)}
                onClick={() => setForm((current) => ({
                  ...current,
                  destination_preferences: toggleProfileValue(current.destination_preferences, value),
                }))}
              >
                {translateLabel(value)}
              </ChoiceChip>
            ))}
          </div>
        </PreferenceCard>

        {error && (
          <p className="md:col-span-2 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-300" data-testid="profile-error">
            {error}
          </p>
        )}

        <div className="flex flex-col-reverse gap-3 md:col-span-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={onClear}
            disabled={saving || !hasRiderProfilePreferences(form)}
            className="rounded-full border border-white/10 px-5 py-3 text-sm font-bold text-zinc-300 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="profile-clear"
          >
            {translateLabel("Clear preferences")}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="rounded-full bg-[#CCFF00] px-7 py-3 text-sm font-extrabold text-black disabled:opacity-60"
            data-testid="profile-save"
          >
            {translateLabel(saving ? "Saving…" : "Save ride preferences")}
          </button>
        </div>
      </div>

      <aside className="h-fit overflow-hidden rounded-3xl border border-[#CCFF00]/20 bg-[linear-gradient(180deg,rgba(204,255,0,0.075),rgba(18,18,20,0.98)_30%)] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.2)] xl:sticky xl:top-24">
        <div className="flex items-center gap-2 text-[#CCFF00]">
          <SlidersHorizontalIcon size={19} weight="bold" />
          <span className="text-[11px] font-bold uppercase tracking-[0.18em]">{translateLabel("Preference summary")}</span>
        </div>
        <h2 className="mt-3 font-display text-2xl font-bold uppercase text-white">{translateLabel("What BPF knows")}</h2>

        {summary.length ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {summary.map((item) => <SummaryTag key={item}>{item}</SummaryTag>)}
          </div>
        ) : (
          <p className="mt-4 text-sm leading-6 text-zinc-500">{translateLabel("No persistent preferences yet. Discovery still works normally.")}</p>
        )}

        <div className="mt-6 border-t border-white/10 pt-5">
          <h3 className="font-bold text-white">{translateLabel("How this affects results")}</h3>
          <p className="mt-2 text-sm leading-6 text-zinc-400">
            {translateLabel("Preferences help BPF rank and explain recommendations. They do not hide destinations unless you explicitly use a strict Discovery filter.")}
          </p>
        </div>
      </aside>
    </div>
  );
}

export default function RiderProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "preferences" ? "preferences" : "profile";

  const [form, setForm] = useState(emptyRiderProfileForm());
  const [homeLocation, setHomeLocation] = useState(null);
  const [personalMap, setPersonalMap] = useState([]);
  const [garage, setGarage] = useState({ photos: [], bikes: [], photo_limit: 10, bike_limit: 10 });
  const [garageBusy, setGarageBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user === null) return;
    if (user === false) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      api.get("/rider-profile"),
      api.get("/personal-map").catch(() => ({ data: [] })),
      api.get("/profile-garage").catch(() => ({ data: { photos: [], bikes: [], photo_limit: 10, bike_limit: 10 } })),
    ])
      .then(([profileResult, mapResult, garageResult]) => {
        if (!active) return;
        setForm(profileToForm(profileResult.data));
        setHomeLocation(profileResult.data?.home_location || null);
        setPersonalMap(mapResult.data || []);
        setGarage(garageResult.data || { photos: [], bikes: [], photo_limit: 10, bike_limit: 10 });
      })
      .catch((err) => {
        if (active) setError(err.response?.data?.detail || err.message || "Could not load Rider Profile");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [user]);

  const setTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "preferences") next.set("tab", "preferences");
    else next.delete("tab");
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/rider-profile", formToRiderProfileInput(form));
      setForm(profileToForm(data));
      toast.success(translateLabel("Ride preferences saved"));
    } catch (err) {
      const message = err.response?.data?.detail || err.message || "Could not save Ride Preferences";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const requestVerification = async () => {
    setSendingVerification(true);
    try {
      const { data } = await api.post("/auth/request-email-verification");
      if (data.ok) toast.success(translateLabel("Verification email sent"));
      else toast(translateLabel("No new email was sent. Try again in a minute."));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || translateLabel("Could not send verification email"));
    } finally {
      setSendingVerification(false);
    }
  };

  const reloadGarage = async () => {
    const { data } = await api.get("/profile-garage");
    setGarage(data);
  };

  const uploadProfilePhoto = async (file) => {
    setGarageBusy(true);
    try {
      await api.uploadProfilePhoto({ file });
      await reloadGarage();
      toast.success(translateLabel("Profile photo uploaded"));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || err.message || "Could not upload profile photo");
    } finally {
      setGarageBusy(false);
    }
  };

  const setProfileHero = async (photoId) => {
    setGarageBusy(true);
    try {
      await api.post("/profile-garage/hero", { photo_id: photoId });
      await reloadGarage();
      toast.success(translateLabel("Profile hero updated"));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not update profile hero");
    } finally {
      setGarageBusy(false);
    }
  };

  const removeProfilePhoto = async (photoId) => {
    setGarageBusy(true);
    try {
      await api.delete("/profile-garage/photo", { params: { photo_id: photoId } });
      await reloadGarage();
      toast.success(translateLabel("Profile photo removed"));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not remove profile photo");
    } finally {
      setGarageBusy(false);
    }
  };

  const saveBike = async (bike) => {
    setGarageBusy(true);
    try {
      await api.post("/profile-garage/bikes", bike);
      await reloadGarage();
      toast.success(translateLabel("Bike added to Garage"));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not save bike");
      throw err;
    } finally {
      setGarageBusy(false);
    }
  };

  const removeBike = async (bikeId) => {
    setGarageBusy(true);
    try {
      await api.delete("/profile-garage/bike", { params: { bike_id: bikeId } });
      await reloadGarage();
      toast.success(translateLabel("Bike removed"));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not remove bike");
    } finally {
      setGarageBusy(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    setError("");
    try {
      const cleared = emptyRiderProfileForm();
      await api.post("/rider-profile", formToRiderProfileInput(cleared));
      setForm(cleared);
      toast.success(translateLabel("Ride preferences cleared"));
    } catch (err) {
      const message = err.response?.data?.detail || err.message || "Could not clear Ride Preferences";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (user === false) {
    return (
      <AppShell size="form">
        <Surface className="px-6 py-16 text-center">
          <UserCircleIcon size={54} className="mx-auto text-zinc-600" />
          <h1 className="mt-5 font-display text-3xl font-black uppercase text-white">{translateLabel("Your Rider Profile")}</h1>
          <p className="mx-auto mt-3 max-w-md text-sm text-zinc-400">
            Your Rider Profile is private. Sign in to save preferences, visited parks and your personal map.
          </p>
          <button
            onClick={() => navigate("/login?returnTo=%2Fprofile")}
            className="mt-6 rounded-full bg-[#CCFF00] px-6 py-3 text-sm font-bold text-black"
            data-testid="profile-sign-in"
          >
            {translateLabel("Sign in")}
          </button>
        </Surface>
      </AppShell>
    );
  }

  return (
    <AppShell size="canvas" contentClassName="overflow-x-hidden px-3 pb-16 md:px-6">
      <div className="min-w-0 pt-1 md:pt-5">
        <div className="min-w-0 max-w-6xl">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">
            <UserCircleIcon size={15} weight="bold" /> {translateLabel("Your BPF identity")}
          </div>
          <h1 className="mt-2 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight text-white sm:text-5xl md:text-7xl">{translateLabel("Rider Profile")}</h1>
          <p className="mt-2 max-w-none text-sm leading-5 text-zinc-400 md:whitespace-nowrap md:text-base md:leading-6">
            {translateLabel("Your profile, saved destinations and persistent ride preferences — all in one place.")}
          </p>
        </div>

        <div className="mt-5 flex w-full min-w-0 rounded-2xl border border-white/10 bg-[#121214]/95 p-1 shadow-[0_10px_35px_rgba(0,0,0,0.14)] sm:mt-7 sm:w-fit">
          {TAB_OPTIONS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              data-testid={`rider-profile-tab-${id}`}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold transition-colors sm:flex-none sm:px-6 ${
                activeTab === id ? "bg-[#CCFF00] text-black" : "text-zinc-400 hover:bg-white/5 hover:text-white"
              }`}
            >
              <Icon size={17} weight="bold" /> {translateLabel(label)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        {loading || user === null ? (
          <div className="space-y-4">
            <Skeleton className="h-64 rounded-3xl bg-white/5" />
            <div className="grid gap-4 md:grid-cols-2">
              <Skeleton className="h-64 rounded-2xl bg-white/5" />
              <Skeleton className="h-64 rounded-2xl bg-white/5" />
            </div>
          </div>
        ) : activeTab === "profile" ? (
          <ProfileTab
            user={user}
            form={form}
            personalMap={personalMap}
            garage={garage}
            garageBusy={garageBusy}
            homeLocation={homeLocation}
            onHomeLocationChange={setHomeLocation}
            onPreferences={() => setTab("preferences")}
            onVerifyEmail={requestVerification}
            sendingVerification={sendingVerification}
            onUploadPhoto={uploadProfilePhoto}
            onSetHero={setProfileHero}
            onRemovePhoto={removeProfilePhoto}
            onSaveBike={saveBike}
            onRemoveBike={removeBike}
          />
        ) : (
          <PreferencesTab
            form={form}
            setForm={setForm}
            saving={saving}
            onSave={save}
            onClear={clear}
            error={error}
          />
        )}
      </div>
    </AppShell>
  );
}
